import { apiClient } from '@/sdk/shared/apiClient';
import { getApiBaseUrl } from '@/lib/env';
import type {
  Wallet,
  CreateWalletRequest,
  UpdateBalanceRequest,
  UpdatePendingBalanceRequest,
  WalletResponse,
  DepositTransaction,
  CreateDepositResponse,
  DepositListResponse,
  ApproveRejectRequest,
  ApproveRejectResponse,
  AdminDepositFilters,
  AdminDepositsResponse,
  AdminDepositsListResponse,
  DepositStats,
  DepositStatsResponse,
  PaymentProofUrlResponse,
} from './types';

export const adminWalletApi = {
  // Wallet Management
  createWallet: async (data: CreateWalletRequest): Promise<Wallet> => {
    const response = await apiClient.post<WalletResponse>('/wallet', data);
    return response.data;
  },

  updateBalance: async (
    clientId: string,
    data: UpdateBalanceRequest,
    tenantSlug?: string
  ): Promise<Wallet> => {
    const params = tenantSlug ? `?tenantSlug=${tenantSlug}` : '';
    const response = await apiClient.patch<WalletResponse>(
      `/wallet/${clientId}/balance${params}`,
      data
    );
    return response.data;
  },

  updatePendingBalance: async (
    clientId: string,
    data: UpdatePendingBalanceRequest,
    tenantSlug?: string
  ): Promise<Wallet> => {
    const params = tenantSlug ? `?tenantSlug=${tenantSlug}` : '';
    const response = await apiClient.patch<WalletResponse>(
      `/wallet/${clientId}/pending${params}`,
      data
    );
    return response.data;
  },

  getWalletsByTenant: async (
    tenantSlug: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<Wallet[]> => {
    const response = await apiClient.get<{ message: string; data: Wallet[] }>(
      `/wallet/tenant/${tenantSlug}?limit=${limit}&offset=${offset}`
    );
    return response.data;
  },

  // Deposit Management (Admin Only - RBAC protected)
  getPendingDeposits: async (
    tenantSlug: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<any> => {
    const response = await apiClient.get<any>(
      `/admin/deposit/pending?tenantSlug=${tenantSlug}&limit=${limit}&offset=${offset}`
    );
    return response.data;
  },

  approveDeposit: async (
    transactionId: string,
    tenantSlug: string,
    adminUserId?: string
  ): Promise<ApproveRejectResponse['data']> => {
    const response = await apiClient.post<ApproveRejectResponse>(
      `/admin/deposit/${transactionId}/approve?tenantSlug=${tenantSlug}`,
      { adminUserId }
    );
    return response.data;
  },

  rejectDeposit: async (
    transactionId: string,
    tenantSlug: string,
    adminUserId?: string
  ): Promise<ApproveRejectResponse['data']> => {
    const response = await apiClient.post<ApproveRejectResponse>(
      `/admin/deposit/${transactionId}/reject?tenantSlug=${tenantSlug}`,
      { adminUserId }
    );
    return response.data;
  },

  editInrAmount: async (
    transactionId: string,
    inrAmount: number,
    tenantSlug: string
  ): Promise<any> => {
    const response = await apiClient.patch<any>(
      `/admin/deposit/${transactionId}/inr?tenantSlug=${tenantSlug}`,
      { inrAmount }
    );
    return response.data;
  },

  getAllDepositsForAdmin: async (
    tenantSlug: string,
    filters: AdminDepositFilters = {},
    limit: number = 50,
    offset: number = 0
  ): Promise<any> => {
    const params = new URLSearchParams({
      tenantSlug,
      limit: limit.toString(),
      offset: offset.toString(),
    });

    if (filters.status && filters.status !== 'all') {
      params.append('status', filters.status);
    }
    if (filters.paymentMethod && filters.paymentMethod !== 'all') {
      params.append('paymentMethod', filters.paymentMethod);
    }
    if (filters.search) {
      params.append('search', filters.search);
    }

    const response = await apiClient.get<any>(
      `/admin/deposit/documents?${params.toString()}`
    );
    return response.data;
  },

  getDepositStats: async (tenantSlug: string): Promise<DepositStats> => {
    const response = await apiClient.get<DepositStatsResponse>(
      `/admin/deposit/stats?tenantSlug=${tenantSlug}`
    );
    return response.data;
  },

  getDepositCounts: async (tenantSlug: string): Promise<any> => {
    const response = await apiClient.get<any>(
      `/admin/deposit/counts?tenantSlug=${tenantSlug}`
    );
    return response.data;
  },

  getPaymentProofUrl: async (
    transactionId: string,
    tenantSlug: string
  ): Promise<PaymentProofUrlResponse['data']> => {
    const response = await apiClient.get<PaymentProofUrlResponse>(
      `/admin/deposit/${transactionId}/payment-proof?tenantSlug=${tenantSlug}`
    );
    return response.data;
  },

  getPaymentProofImage: async (
    transactionId: string,
    tenantSlug: string
  ): Promise<Blob> => {
    const token = apiClient.getToken();
    const baseUrl = getApiBaseUrl();
    const response = await fetch(
      `${baseUrl}/admin/deposit/${transactionId}/payment-proof/image?tenantSlug=${tenantSlug}`,
      {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
      }
    );
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'An error occurred' }));
      throw { status: response.status, ...error };
    }
    return response.blob();
  },
};

export default adminWalletApi;
